/**
 * Two things live here, both scoped to the cart page/drawer:
 *
 * 1. <inkora-package-quantity> — a +/- stepper for the custom-size sticker
 *    product, where the real Shopify line quantity is actually the total
 *    price in cents (see inkora-buy-buttons-custom-size.liquid), not a
 *    sticker count. Each click has to move that real quantity by one whole
 *    package's price (data-unit-cents), while the visible number stays a
 *    plain package count.
 *
 * 2. An Artwork Assist Add-On sync that runs after every cart change,
 *    regardless of what caused it (this stepper, the remove button, the
 *    products' own normal quantity input, etc). It recomputes the add-on
 *    line's quantity as the NUMBER OF LINES that requested "Artwork Assist"
 *    — one fee per product line, however many units of it are ordered. If a
 *    merchant gives a future product the same "Artwork Assist" property
 *    name, it's automatically included with no extra code.
 */

const INKORA_ARTWORK_ASSIST_PRODUCT_ID = 9047944364230;

function inkoraGetCartItemsElement() {
  return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
}

function inkoraPackageCount(item) {
  const unitCents = parseInt(item.properties && item.properties['_Unit Price Cents'], 10);
  if (unitCents > 0) {
    return Math.round(item.quantity / unitCents);
  }
  return item.quantity;
}

// Idempotent by design: safe to call after ANY cart change, including ones
// this same function just made — it only calls updateQuantity when the
// add-on's quantity actually needs to change, so it settles after one pass.
async function inkoraSyncArtworkAssistAddon() {
  const cartItems = inkoraGetCartItemsElement();
  if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

  let cart;
  try {
    cart = await fetch('/cart.js').then((response) => response.json());
  } catch (error) {
    console.error(error);
    return;
  }

  // One Artwork Assist fee per product LINE that requested it, no matter how
  // many units of that product the customer orders (owner rule, 2026-10-03).
  let totalPackages = 0;
  let addonLine = null;
  let addonIndex = null;

  cart.items.forEach((item, index) => {
    if (item.properties && item.properties['Artwork Assist']) {
      totalPackages += 1;
    }
    if (item.product_id === INKORA_ARTWORK_ASSIST_PRODUCT_ID) {
      addonLine = item;
      addonIndex = index + 1;
    }
  });

  if (addonLine && addonLine.quantity !== totalPackages) {
    cartItems.updateQuantity(addonIndex, totalPackages, 'inkora-assist-sync', addonLine.variant_id);
  }
}

if (!customElements.get('inkora-package-quantity')) {
  customElements.define(
    'inkora-package-quantity',
    class extends HTMLElement {
      connectedCallback() {
        this.line = this.dataset.line;
        this.unitCents = parseInt(this.dataset.unitCents, 10);
        this.variantId = this.dataset.variantId;
        this.display = this.querySelector('[data-inkora-package-count]');
        this.querySelector('[name="minus"]').addEventListener('click', () => this.changeBy(-1));
        this.querySelector('[name="plus"]').addEventListener('click', () => this.changeBy(1));
      }

      get packageCount() {
        return parseInt(this.display.textContent, 10) || 0;
      }

      changeBy(delta) {
        const cartItems = inkoraGetCartItemsElement();
        if (!cartItems) return;
        const newCount = Math.max(0, this.packageCount + delta);
        const newQuantity = newCount * this.unitCents;
        cartItems.updateQuantity(this.line, newQuantity, delta > 0 ? 'plus' : 'minus', this.variantId);
      }
    }
  );
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-inkora-empty-cart]');
  if (!button) return;
  if (!window.confirm('Remove everything from your cart?')) return;

  button.disabled = true;
  fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
    .then(() => window.location.reload())
    .catch((error) => {
      console.error(error);
      button.disabled = false;
    });
});

/**
 * "Rush my order" — flat-fee add-on (product handle: rush-production).
 *
 * Rush used to be a "Production Time" variant on every fixed-size sticker
 * product. It is now a single add-on line the customer ticks in the cart
 * (card rendered by snippets/inkora-rush-toggle.liquid). Ticking adds one unit
 * of the add-on variant; unticking (or removing the line) takes it out.
 */
const INKORA_RUSH_HANDLE = 'rush-production';

// A line Rush can apply to: any real product (fixed-size AND custom-size
// builder), i.e. everything except the add-ons themselves.
function inkoraIsRushEligibleItem(item) {
  return item.product_id !== INKORA_ARTWORK_ASSIST_PRODUCT_ID && item.handle !== INKORA_RUSH_HANDLE;
}

function inkoraIsCartPage() {
  return window.location.pathname.replace(/\/$/, '').endsWith('/cart');
}

async function inkoraRefreshCartBubble() {
  try {
    const html = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`).then((r) => r.text());
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    const target = document.querySelector('#cart-icon-bubble .shopify-section') || document.getElementById('cart-icon-bubble');
    if (source && target) target.innerHTML = source.innerHTML;
  } catch (error) {
    console.error(error);
  }
}

// On the cart page the totals/footer are separate sections, so the simplest
// way to keep every number honest is a reload; the drawer re-renders itself
// through Dawn's own cartUpdate handling (CartItems.onCartUpdate).
function inkoraAfterRushChange() {
  if (inkoraIsCartPage()) {
    window.location.reload();
    return;
  }
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'inkora-rush' });
  }
  inkoraRefreshCartBubble();
}

document.addEventListener('change', async (event) => {
  const box = event.target.closest && event.target.closest('[data-inkora-rush]');
  if (!box) return;

  const wantsRush = box.checked;
  const variantId = box.dataset.variantId;
  box.disabled = true;

  try {
    const response = await fetch(wantsRush ? window.routes.cart_add_url : window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(
        wantsRush ? { items: [{ id: Number(variantId), quantity: 1 }] } : { id: String(variantId), quantity: 0 }
      ),
    }).then((r) => r.json());

    if (response && (response.status || response.errors)) throw new Error(response.description || 'Rush update failed');
  } catch (error) {
    console.error(error);
    box.checked = !wantsRush; // put the checkbox back to what the cart really holds
    box.disabled = false;
    return;
  }

  inkoraAfterRushChange();
});

// If every real product is removed, a lone "Rush Production" charge makes no
// sense (it would also leave the cart looking non-empty) — take it out too.
// Idempotent: does nothing unless that exact situation exists.
async function inkoraSyncRushAddon() {
  let cart;
  try {
    cart = await fetch('/cart.js').then((response) => response.json());
  } catch (error) {
    console.error(error);
    return;
  }

  const rushLine = cart.items.find((item) => item.handle === INKORA_RUSH_HANDLE);
  if (!rushLine || cart.items.some(inkoraIsRushEligibleItem)) return;

  try {
    await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(rushLine.variant_id), quantity: 0 }),
    });
    inkoraAfterRushChange();
  } catch (error) {
    console.error(error);
  }
}

// Runs after every cart update on the page (Dawn's cart.js/cart-drawer.js
// publish this from CartItems.updateQuantity — including the removal
// button, quantity-input changes on ordinary products, and the stepper
// above) so a removed or changed line always leaves the add-on quantity
// correct without needing to special-case every possible trigger.
// Also settle the add-on when the cart page first loads: adding the same
// product twice from its product page adds the add-on twice, and no
// cartUpdate event fires for that. Idempotent, so it's a no-op when correct.
window.addEventListener('load', () => {
  if (inkoraIsCartPage()) setTimeout(inkoraSyncArtworkAssistAddon, 500);
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    inkoraSyncArtworkAssistAddon();
    inkoraSyncRushAddon();
  });
}
